/**
 * The single HTTP transport, and the seam every generated API call routes through.
 *
 * `orval.config.ts` points its `mutator` at `customInstance` here, so the auth behaviour
 * exists exactly once (foundation ADR §2, "`src/api/` root files — the mutator: token
 * attachment, single-flight 401 refresh, ProblemDetails-to-error mapping. One file, one
 * seam, injectable for tests").
 *
 * The contract, from the authentication ADR §4 — reproduced from mobile's `src/api/http.ts`,
 * not imported from it:
 *   · attach the current bearer to every authenticated request;
 *   · on a 401, call the refresh seam exactly once and, if it yields a token, retry the
 *     request exactly once;
 *   · never decide sign-in or sign-out at the transport layer — report the outcome and let
 *     the session layer distinguish a degraded session from a terminated one. Forcing a
 *     logout from here is the defect that produced the 2026-07-22 session-loss incident.
 *
 * Note where single-flight lives: NOT here. `deps.refreshAccessToken` must itself be the
 * single-flight seam (mobile's `refreshAccessToken` holds a module-level in-flight promise),
 * so a burst of concurrent 401s across the app collapses into one
 * `supabase.auth.refreshSession()` round trip. A transport-level guard would only cover
 * calls that happen to pass through this module.
 *
 * Nothing in this file reads `import.meta.env` or touches Supabase. The authentication
 * feature owns `configureApi()` and supplies both; until it does, `customInstance` throws
 * rather than sending an unauthenticated request that would fail confusingly later.
 */

/** RFC 9457 problem body. `code` is the backend's stable discriminator (`Domain/AuthErrorCodes.cs`). */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /**
   * Route on THIS, never on status plus `type`. The backend is explicit about it:
   * `profile_missing` arrives as 403 from a role-gated endpoint and 404 from `GET /auth/me`.
   */
  code?: string;
  errors?: Record<string, string[]>;
  /**
   * The request field a refusal is about, where the backend names one — the credit ledger's writes
   * do (`CreditLedgerProcessor.Refuse`: `amount`, `note`, `calendarEventId`).
   */
  field?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | undefined;
  /**
   * How long the server asked to be left alone, in seconds (`Retry-After` on a 429 or a 503), or
   * `null` when it named nothing or named a date. A caller that sends again waits at least this.
   */
  readonly retryAfterSeconds: number | null;

  constructor(status: number, problem?: ProblemDetails, retryAfterSeconds: number | null = null) {
    super(problem?.title ?? problem?.detail ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** The backend's stable error code, when it sent one. Many 400s carry none — handle that. */
  get code(): string | undefined {
    return this.problem?.code;
  }
}

/**
 * A 404 or a 403: the resource was deleted or is not this trainer's. Cross-tenant reads answer 404
 * by design and a 403 collapses into it, so either is an answer rather than a failure to retry.
 */
export function isGone(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error.status === 404 || error.status === 403)
  );
}

export class ApiNotConfiguredError extends Error {
  constructor() {
    super(
      'The API transport is not configured. Call configureApi() from the authentication ' +
        'feature during app start-up before any generated hook runs.',
    );
    this.name = 'ApiNotConfiguredError';
  }
}

/** The request shape orval emits as the mutator's first argument, in `httpClient: 'axios'` mode. */
export interface RequestConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Present in orval's config shape; unused — every endpoint is JSON or empty. */
  responseType?: string;
}

/** Per-call options; orval forwards these as the mutator's SECOND argument. */
export interface RequestOptions {
  /**
   * Anonymous endpoint: send no bearer and do not run the 401 → refresh → retry path.
   * Set it for the anonymous verbs (`POST /auth/register-trainer`, invitation acceptance) so a
   * bad credential cannot trip a refresh against a session that does not exist.
   */
  skipAuth?: boolean;
  /**
   * Let the request outlive the page (`fetch`'s `keepalive`). For the one save that runs as a page is
   * hidden or closed: the public form's answers on their way (`features/public-form-runner`), and a
   * Buddy draft's unsaved edits (`pages/ai-plan-builder`). The browser refuses a keepalive body above
   * 64 KB: a survey's answers stay far under it, and the draft's saver measures its body and sends
   * nothing larger.
   */
  keepalive?: boolean;
}

/** Auth outcomes the session layer subscribes to. The transport reports; it never routes UX. */
export interface ApiAuthEvents {
  /** An authenticated call 401'd even after the refresh and retry, or refresh yielded no token. */
  onAuthTransientFailure(info: { status: number; code: string | undefined }): void;
  /** An authenticated call returned 2xx — proof the session works right now. */
  onAuthenticatedSuccess(): void;
}

export interface ApiDeps {
  getBaseUrl(): string;
  getAccessToken(): Promise<string | null>;
  /** MUST be the single-flight refresh seam — see the header note. */
  refreshAccessToken(): Promise<string | null>;
  authEvents?: ApiAuthEvents;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchFn?: typeof fetch;
}

/**
 * Stringify one query-parameter value. Explicit rather than `String(value)`: the generated
 * operations pass primitives, `Date`s and enum members, and a bare `String()` on anything else
 * would silently put `[object Object]` on the wire — a filter that quietly matches nothing.
 */
function toQueryValue(value: NonNullable<unknown> | null): string {
  if (value === null) return 'null';
  if (value instanceof Date) return value.toISOString();
  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
    case 'boolean':
    case 'bigint':
      return value.toString();
    default:
      return JSON.stringify(value);
  }
}

/** Serialise `params` onto the URL. Arrays repeat the key; `undefined` values are dropped. */
function resolveUrl(config: RequestConfig, baseUrl: string): string {
  const base = config.url.startsWith('http') ? config.url : `${baseUrl}${config.url}`;
  if (!config.params) return base;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(config.params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value as unknown[]) {
        if (item !== undefined) search.append(key, toQueryValue(item));
      }
      continue;
    }
    search.append(key, toQueryValue(value));
  }

  const query = search.toString();
  return query ? `${base}?${query}` : base;
}

function buildInit(config: RequestConfig, token: string | null, keepalive: boolean): RequestInit {
  const headers = new Headers(config.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // Multipart branch: the browser must set the boundary itself, so send FormData raw and
  // strip any Content-Type. Passing the same FormData through twice on the retry is safe —
  // it is handed to fetch, never consumed here.
  if (config.data instanceof FormData) {
    headers.delete('Content-Type');
    return { method: config.method, headers, body: config.data, signal: config.signal, keepalive };
  }

  // Raw binary branch (`POST /ai/transcriptions`): the bytes go as they are. orval writes the
  // FIRST media type the operation declares into the header, so the Blob's own type wins over it —
  // a WebM recording labelled `audio/mp4` would be a lie the server is entitled to refuse.
  if (config.data instanceof Blob) {
    if (config.data.type !== '') headers.set('Content-Type', config.data.type);
    return { method: config.method, headers, body: config.data, signal: config.signal, keepalive };
  }

  if (config.data !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return {
    method: config.method,
    headers,
    body: config.data === undefined ? undefined : JSON.stringify(config.data),
    signal: config.signal,
    keepalive,
  };
}

async function readProblem(response: Response): Promise<ProblemDetails | undefined> {
  try {
    return (await response.json()) as ProblemDetails;
  } catch {
    // A 502 from a proxy, or an empty body. Not every failure is a ProblemDetails.
    return undefined;
  }
}

/**
 * The transport, as a factory over its dependencies. Exported so a test drives it directly
 * with a scripted `fetchFn` and no module-level state.
 */
export function createApiRequest(deps: ApiDeps) {
  const fetchFn = deps.fetchFn ?? globalThis.fetch;

  return async function request<T>(
    config: RequestConfig,
    options: RequestOptions = {},
  ): Promise<T> {
    const skipAuth = options.skipAuth ?? false;
    const keepalive = options.keepalive ?? false;
    const url = resolveUrl(config, deps.getBaseUrl());

    const token = skipAuth ? null : await deps.getAccessToken();
    let response = await fetchFn(url, buildInit(config, token, keepalive));

    if (response.status === 401 && !skipAuth) {
      const refreshedToken = await deps.refreshAccessToken();
      if (refreshedToken) {
        response = await fetchFn(url, buildInit(config, refreshedToken, keepalive));
      }
    }

    if (!response.ok) {
      const problem = await readProblem(response);
      if (response.status === 401 && !skipAuth) {
        // Report, do not decide. A refresh failure is not terminal: supabase-js emits
        // SIGNED_OUT itself when the failure is non-retryable against an expired token.
        deps.authEvents?.onAuthTransientFailure({ status: response.status, code: problem?.code });
      }
      throw new ApiError(response.status, problem, retryAfterSeconds(response.headers.get('retry-after')));
    }

    if (!skipAuth) deps.authEvents?.onAuthenticatedSuccess();

    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');
    if (
      response.status === 204 ||
      contentLength === '0' ||
      !contentType?.includes('application/json')
    ) {
      return undefined as T;
    }

    return (await response.json()) as T;
  };
}

/** `Retry-After` as seconds: the delta-seconds form only; the HTTP-date form is not used by this backend. */
function retryAfterSeconds(header: string | null): number | null {
  if (header === null || !/^\d{1,6}$/.test(header.trim())) return null;
  return Number(header.trim());
}

let request: ReturnType<typeof createApiRequest> | null = null;
let wiredDeps: ApiDeps | null = null;

/** Wire the transport. Called once, by the authentication feature, during app start-up. */
export function configureApi(deps: ApiDeps): void {
  request = createApiRequest(deps);
  wiredDeps = deps;
}

/**
 * What a socket needs from the same wiring every fetch uses: the API's origin and the bearer, read
 * fresh on each (re)connect. The chat hub (`/hubs/chat`) cannot take the header, so SignalR puts
 * the token in `access_token`, which the backend reads on that one path only. `null` before
 * `configureApi` ran.
 */
export function realtimeTransport(): {
  readonly baseUrl: string;
  readonly accessToken: () => Promise<string | null>;
} | null {
  const deps = wiredDeps;
  if (deps === null) return null;
  return { baseUrl: deps.getBaseUrl(), accessToken: () => deps.getAccessToken() };
}

/** The function every generated orval operation calls. */
export function customInstance<T>(config: RequestConfig, options?: RequestOptions): Promise<T> {
  if (!request) throw new ApiNotConfiguredError();
  return request<T>(config, options);
}

/**
 * orval references these for hook error and body typing. Every failure this transport throws
 * is an `ApiError`, so the generated `E` type parameter is deliberately discarded — a hook's
 * `error` is typed as what it can actually receive.
 */
export type ErrorType<_E = unknown> = ApiError;
export type BodyType<B> = B;
